import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface ContributionSettings {
  minContributionAmount: number | null;
  maxContributionAmount: number | null;
}

interface ContributionSettingsCardProps {
  settings: ContributionSettings;
  onUpdate: (settings: ContributionSettings) => void;
}

const ContributionSettingsCard = ({ settings, onUpdate }: ContributionSettingsCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ContributionSettings>({
    minContributionAmount: settings.minContributionAmount,
    maxContributionAmount: settings.maxContributionAmount,
  });
  const [errors, setErrors] = useState<{ min?: string; max?: string }>({});

  const handleSave = () => {
    const newErrors: { min?: string; max?: string } = {};

    // Validate min amount
    if (formData.minContributionAmount !== null && formData.minContributionAmount < 0) {
      newErrors.min = 'Minimum amount cannot be negative';
    }

    // Validate max amount
    if (formData.maxContributionAmount !== null && formData.maxContributionAmount < 0) {
      newErrors.max = 'Maximum amount cannot be negative';
    }

    // Validate that max >= min if both are set
    if (
      formData.minContributionAmount !== null &&
      formData.maxContributionAmount !== null &&
      formData.maxContributionAmount < formData.minContributionAmount
    ) {
      newErrors.max = 'Maximum amount must be greater than or equal to minimum amount';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onUpdate(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      minContributionAmount: settings.minContributionAmount,
      maxContributionAmount: settings.maxContributionAmount,
    });
    setErrors({});
    setIsEditing(false);
  };

  const formatAmount = (amount: number | null) => {
    if (amount === null) return 'No limit';
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VT`;
  };

  return (
    <div className="bg-card rounded-md border border-border p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center">
            <Icon name="DollarSign" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Contribution Amount Limits</h2>
            <p className="text-sm text-muted-foreground">
              Set minimum and maximum contribution amounts per transaction
            </p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>

      <div className="space-y-6">
        {isEditing ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Minimum Contribution Amount (VT)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.minContributionAmount ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseFloat(e.target.value);
                    setFormData({ ...formData, minContributionAmount: value });
                    if (errors.min) setErrors({ ...errors, min: undefined });
                  }}
                  placeholder="No minimum"
                  className={`w-full px-3 py-2 border rounded-sm bg-background text-foreground ${
                    errors.min ? 'border-destructive' : 'border-input'
                  }`}
                />
                {errors.min && (
                  <p className="text-sm text-destructive mt-1">{errors.min}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty for no minimum limit
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Maximum Contribution Amount (VT)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.maxContributionAmount ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseFloat(e.target.value);
                    setFormData({ ...formData, maxContributionAmount: value });
                    if (errors.max) setErrors({ ...errors, max: undefined });
                  }}
                  placeholder="No maximum"
                  className={`w-full px-3 py-2 border rounded-sm bg-background text-foreground ${
                    errors.max ? 'border-destructive' : 'border-input'
                  }`}
                />
                {errors.max && (
                  <p className="text-sm text-destructive mt-1">{errors.max}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty for no maximum limit
                </p>
              </div>
            </div>

            <div className="bg-muted/50 rounded-sm p-4">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={18} className="text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-1">How it works:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>When recording contributions, amounts outside these limits will be rejected</li>
                    <li>If both limits are set, maximum must be greater than or equal to minimum</li>
                    <li>Leave fields empty to allow any amount</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="default" onClick={handleSave}>
                Save Settings
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Minimum Amount
                </label>
                <p className="text-base text-foreground mt-1">
                  {formatAmount(settings.minContributionAmount)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Maximum Amount
                </label>
                <p className="text-base text-foreground mt-1">
                  {formatAmount(settings.maxContributionAmount)}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContributionSettingsCard;

